// The machine as a tab (canvas 4b "dev1", opened from the ■ ON card's See all details): who it is and where, its
// load over the last hour in one usage block (disk as a meter) and the round trip from here, what runs on it busiest first,
// and the ports it serves.

import { ProcessIcon } from '../ProcessIcon';
import { PixelMeter } from '../PixelMeter';
import { gb, ofGb, pct, Spark } from '../Usage';
import { IconAdjustmentsHorizontal, IconArrowUpRight, IconRefresh, IconShare } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { machineUsage, type Usage, type UsageRange } from '../../api';
import { conn } from '../../net/connection';
import { holdConnection } from '../../net/live';
import type { MachineActivity } from '../../net/protocol';
import { telemetry, type Stats } from '../../net/telemetry';
import { ChangeTier, RebootControl } from '../../pages/MachineControls';
import { useStore } from '../../store';
import { Button, Empty, Ic, Mark, Meta, Mini, Mono, Pix } from '../ds';
import { loadLook, regionCity } from '../Machine';
import { ShareDialog } from '../Share';
import { statusMark } from '../PaneBar';
import { stateWords } from '../time';
import { useActions } from '../Workspace';

const ms = (v: number | null) => (v === null ? '–' : `${v < 10 ? v.toFixed(1) : Math.round(v)} ms`);
/** Bytes as the dashboard's usage panel prints them (fmtBytes): 1024 steps, one decimal under 10. */
function bytes(n: number) {
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n < 10 && i > 0 ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}
const rate = (n: number) => `${bytes(n)}/s`;

function up(started: number) {
  const s = Math.max(0, Math.floor(Date.now() / 1000 - started));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
}

let cached: Stats = telemetry.stats();
const subscribeStats = (cb: () => void) =>
  telemetry.subscribe(() => {
    cached = telemetry.stats();
    cb();
  });

function useUsage(range: UsageRange, every: number) {
  const [u, setU] = useState<Usage | null>(null);
  useEffect(() => {
    let live = true;
    const get = () => void machineUsage(range).then((r) => live && r.ok && setU(r.data));
    get();
    const t = setInterval(get, every);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [range, every]);
  return u;
}

/** What runs, every 2 s while the tab is open (the dashboard's Activity asks the same). */
function useActivity() {
  const [a, setA] = useState<MachineActivity | null>(null);
  useEffect(() => {
    const release = holdConnection();
    let alive = true;
    let t: ReturnType<typeof setTimeout> | undefined;
    const look = async () => {
      try {
        const r = await conn.control<Partial<MachineActivity>>('activity');
        if (alive) setA({ processes: r.processes ?? [], windows: r.windows ?? [], ports: r.ports ?? [] });
      } catch {
        /* keep the last look */
      }
      if (alive) t = setTimeout(look, 2000);
    };
    void look();
    return () => {
      alive = false;
      clearTimeout(t);
      release();
    };
  }, []);
  return a;
}

/** The round trip, one sample a second while the tab is open (the hub keeps no history of it). */
function useRttSeries(rtt: number | null) {
  const [vals, setVals] = useState<number[]>([]);
  const last = useRef(rtt);
  last.current = rtt;
  useEffect(() => {
    const t = setInterval(() => last.current !== null && setVals((v) => [...v.slice(-59), last.current!]), 1000);
    return () => clearInterval(t);
  }, []);
  return vals;
}

/** What a stat reads at sample i (while the pointer or the arrow keys are on its chart). */
type Reading = { value: string; sub: string };

/** A sample's time for the note while reading: 14:32, with the day when it's not today. */
function when(t: number) {
  const d = new Date(t * 1000);
  const today = d.toDateString() === new Date().toDateString();
  return d.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit', ...(today ? {} : { weekday: 'short' }) });
}

type StatProps = {
  label: string;
  value: string;
  sub: string;
  vals?: number[];
  alt?: number[];
  /** Each sample's time (seconds); without them the note stays while reading (RTT). */
  times?: number[];
  read?: (i: number) => Reading;
  /** A span other than the block's hour (RTT: since opened), in the cell's corner. */
  note?: string;
  /** A share (0–1) drawn as a meter instead of a chart (disk). */
  share?: number;
  testid?: string;
};
/** One cell of the usage block: label, the number with its sub-line under it, then the chart in a well. */
function Stat({ label, value, sub, vals = [], alt, times, read, note, share, testid }: StatProps) {
  const [at, setAt] = useState<number | null>(null);
  const r = at !== null && at < vals.length && read ? read(at) : null;
  const corner = r && times?.[at!] !== undefined ? when(times[at!]) : note;
  return (
    <div className="ws-stat" data-testid={testid} data-reading={r ? '' : undefined}>
      <div className="ws-stat-top">
        <Pix>{label}</Pix>
        {corner && <Meta>{corner}</Meta>}
      </div>
      <div className="ws-stat-value" aria-live={r ? 'polite' : undefined}>
        <span className="ws-stat-n">{r ? r.value : value}</span>
        <span className="ws-stat-sub">{(r ? r.sub : sub) || '\u00a0'}</span>
      </div>
      {share !== undefined ? (
        <PixelMeter share={share} label={label} className="ws-pixels" />
      ) : (
        <Spark vals={vals} alt={alt} at={r ? at : null} onAt={setAt} label={label} />
      )}
    </div>
  );
}

export function MachineView() {
  const a = useActions();
  const machine = useStore((s) => s.me?.machine);
  const sessions = useStore((s) => s.sessions);
  const ports = useStore((s) => s.ports);
  const [sharing, setSharing] = useState<(typeof ports)[number] | null>(null);
  const rtt = useStore((s) => s.rtt);
  const stats = useSyncExternalStore(subscribeStats, () => cached);
  const hour = useUsage('1h', 10_000);
  const activity = useActivity();
  const rtts = useRttSeries(rtt);
  const [reboot, setReboot] = useState(false);
  const [resize, setResize] = useState(false);
  const badge = loadLook(useStore((s) => s.machineLoad.state));

  const samples = hour?.samples ?? [];
  const latest = hour?.latest;
  const times = samples.map((s) => s.t);
  const procs = useMemo(() => [...(activity?.processes ?? [])].sort((x, y) => y.cpu - x.cpu).slice(0, 8), [activity]);
  const maxCpu = Math.max(20, ...procs.map((p) => p.cpu));

  if (!machine) return null;
  const on = machine.status === 'running';
  const region = machine.region?.match(/\(([^)]+)\)/)?.[1];
  const about = [regionCity(machine.region), region, machine.tier?.toUpperCase()].filter(Boolean).join(' · ');

  return (
    <div className="ws-view ws-mview">
      <div className="ws-mview-head">
        <div className="ws-mview-id">
          <Mini on={on} />
          <h1 className="ws-mview-name">
            <span>{machine.id}</span>
            {about && (
              <span className="ws-mview-about">
                <span aria-hidden="true">//</span>
                {about}
              </span>
            )}
            <span className="ws-badge" data-tone={on ? badge.tone : undefined}>
              <Mark tone={on ? badge.tone : 'idle'} />
              <Mono>{on ? badge.word : machine.status}</Mono>
            </span>
          </h1>
        </div>
        <div className="ws-mview-acts">
          <Button size="sm" onClick={() => setResize(true)} data-testid="machine-resize">
            <Ic icon={IconAdjustmentsHorizontal} size={14} />
            Change size
          </Button>
          <Button size="sm" onClick={() => setReboot(true)}>
            <Ic icon={IconRefresh} size={14} />
            Restart
          </Button>
        </div>
      </div>

      <div className="ws-stats" role="group" aria-label="Usage over the last hour" data-count={latest?.swapTotal ? 7 : 6}>
        {/* what it holds (swap only where there is some), then what moves, then this connection's RTT */}
        <Stat
          label="CPU"
          value={latest ? pct(latest.cpu) : '–'}
          sub={latest ? `of ${latest.cpus} cores` : ''}
          vals={samples.map((s) => s.cpu)}
          times={times}
          read={(i) => ({ value: pct(samples[i].cpu), sub: `of ${samples[i].cpus} cores` })}
        />
        <Stat
          label="Memory"
          value={latest ? gb(latest.memUsed) : '–'}
          sub={latest ? ofGb(latest.memTotal) : ''}
          vals={samples.map((s) => s.memUsed)}
          times={times}
          read={(i) => ({ value: gb(samples[i].memUsed), sub: ofGb(samples[i].memTotal) })}
        />
        {!!latest?.swapTotal && (
          <Stat
            label="Swap"
            testid="stat-swap"
            value={bytes(latest.swapUsed ?? 0)}
            sub={`of ${bytes(latest.swapTotal)}`}
            vals={samples.map((s) => s.swapUsed ?? 0)}
            times={times}
            read={(i) => ({ value: bytes(samples[i].swapUsed ?? 0), sub: `of ${bytes(samples[i].swapTotal ?? 0)}` })}
          />
        )}
        <Stat
          label="Disk"
          value={latest ? gb(latest.diskUsed) : '–'}
          sub={latest ? ofGb(latest.diskTotal) : ''}
          share={latest?.diskTotal ? latest.diskUsed / latest.diskTotal : 0}
        />
        <Stat
          label="Network"
          testid="stat-network"
          value={latest ? `↓ ${rate(latest.netRx)}` : '–'}
          sub={latest ? `↑ ${rate(latest.netTx)}` : ''}
          vals={samples.map((s) => s.netRx)}
          alt={samples.map((s) => s.netTx)}
          times={times}
          read={(i) => ({ value: `↓ ${rate(samples[i].netRx)}`, sub: `↑ ${rate(samples[i].netTx)}` })}
        />
        <Stat
          label="Disk I/O"
          testid="stat-diskio"
          value={latest ? `R ${rate(latest.diskRead)}` : '–'}
          sub={latest ? `W ${rate(latest.diskWrite)}` : ''}
          vals={samples.map((s) => s.diskRead)}
          alt={samples.map((s) => s.diskWrite)}
          times={times}
          read={(i) => ({ value: `R ${rate(samples[i].diskRead)}`, sub: `W ${rate(samples[i].diskWrite)}` })}
        />
        <Stat
          label="RTT"
          value={ms(rtt)}
          sub={stats.echo.p50 !== null ? `echo ${Math.round(stats.echo.p50)} / ${Math.round(stats.echo.p95 ?? 0)} ms` : ''}
          vals={rtts}
          read={(i) => ({ value: ms(rtts[i]), sub: '' })}
          note="Since opened"
        />
      </div>

      <div className="ws-mview-cols">
        <section className="ws-procs" aria-labelledby="mv-procs">
          <div className="ws-sec-head">
            <span className="ws-sec-title">
              <Pix size={11} className="ws-sec-pix">
                <span id="mv-procs">Processes</span>
              </Pix>
              <Mono>{activity ? activity.processes.length : ''}</Mono>
            </span>
            <Meta>By CPU</Meta>
          </div>
          <div className="ws-ptable" role="table" aria-labelledby="mv-procs">
            <div className="ws-prow" role="row" data-head>
              {['Process name', 'State', 'CPU', 'Memory', 'Up'].map((t) => (
                <span key={t} role="columnheader">
                  <Pix>{t}</Pix>
                </span>
              ))}
            </div>
            {procs.map((p) => {
              const s = p.session !== undefined ? sessions.find((x) => x.id === p.session) : undefined;
              const where = s?.row?.meta.none?.where;
              return (
                <div key={p.pid} className="ws-prow" role="row">
                  <span role="cell" className="ws-pname">
                    <ProcessIcon name={p.name} />
                    <span className="ws-pname-n">{s ? s.name : p.name}</span>
                    {s && where && <Meta>{where}</Meta>}
                  </span>
                  <span role="cell" className="ws-pstate" data-tone={s?.status === 'waiting' ? 'attn' : undefined}>
                    {s && (
                      <>
                        <Mark {...statusMark(s.status)} />
                        {stateWords(s).label}
                      </>
                    )}
                  </span>
                  <span role="cell" className="ws-pnum">
                    <span className="ws-pbar" data-load={cpuLoad(p.cpu)} aria-hidden="true">
                      <span style={{ width: `${Math.min(100, (p.cpu / maxCpu) * 100)}%` }} />
                    </span>
                    <span className="ws-pcpu">{pct(p.cpu)}</span>
                  </span>
                  <span role="cell" className="ws-pnum">
                    {gb(p.memory)}
                  </span>
                  <span role="cell" className="ws-pnum" data-muted>
                    {up(p.started)}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
        <aside className="ws-mview-side">
          <section aria-labelledby="mv-ports">
            <div className="ws-sec-head">
              <span className="ws-sec-title">
                <Pix size={11} className="ws-sec-pix">
                  <span id="mv-ports">Ports</span>
                </Pix>
                <Mono>{ports.length}</Mono>
              </span>
            </div>
            {!ports.length && (
              <Empty
                size="sm"
                art="port"
                className="ws-ports-empty"
                data-testid="ports-empty"
                title="Nothing is listening yet"
                lead={`Start a dev server on ${machine.id} and its port shows up here, ready to open in a new tab.`}
              />
            )}
            {ports.map((p) => (
              <div key={p.port} className="ws-portline" data-port={p.port}>
                <button type="button" className="ws-portrow" onClick={() => a.openExternal(p.port)}>
                  <Mark tone="live" />
                  <span className="ws-portrow-n">
                    <Mono size={12} className="ws-portrow-port">
                      :{p.port}
                    </Mono>
                  </span>
                  <Meta>{p.process}</Meta>
                  <span className="ws-portrow-go">
                    <Ic icon={IconArrowUpRight} size={12} />
                  </span>
                </button>
                <Button
                  variant="icon"
                  size="sm"
                  radius={6}
                  aria-label={`Share :${p.port}`}
                  title="Share"
                  aria-haspopup="dialog"
                  data-testid="port-share"
                  onClick={() => setSharing(p)}
                >
                  <Ic icon={IconShare} size={14} />
                </Button>
              </div>
            ))}
            {sharing && <ShareDialog port={sharing.port} process={sharing.process} onClose={() => setSharing(null)} />}
          </section>
        </aside>
      </div>
      {resize && createPortal(<ChangeTier onChanged={() => {}} dialog={{ onClose: () => setResize(false) }} />, document.body)}
      {reboot && createPortal(<RebootControl machineId={machine.id} dialog={{ onClose: () => setReboot(false) }} />, document.body)}
    </div>
  );
}

/** How hard a process works, in percent of one core: half a core and up is busy (attn), nine tenths
 *  and up is hot (fault); below that the bar stays ink. The number beside it says the same. */
export const cpuLoad = (cpu: number): 'busy' | 'hot' | undefined => (cpu >= 90 ? 'hot' : cpu >= 50 ? 'busy' : undefined);
